import { IFramePostMessageType, sendPostMessage } from "./../../utils/helpers";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { login } from "../../services/auth.service";
import { getSubscriptionDetails, updateSubscription } from "../../services/app.service";
import { getAllCondition, getAllSlot, getCheckoutProfileId,getWixSiteType } from "../../services/conditions.service";
import { RootState } from "../store";
import { AppState, SideBarSection, View } from "./appSlice.model";
import { dateFormatYYYYMMDD, subtractDaysAndFormat } from "../../utils/helpers";
import { SECTIONS } from "../../components/Sidebar/Sidebar";
import { PageType } from "@reconvert/reconvert-utils";
import { Platform } from "../../models";
import { APP_PLATFORM, BOOK_DEMO_BANNER_MAX_CAP, LATEST_PLAN_ID } from "../../config";

export const initialView: View = {
  isWidgetView: true,
  isAnalyticView: true,
};

const initialState: AppState = {
  loading: true,
  platformStoreId: "",
  isAppBridgePresent: false,
  isSidebarCollapased: false,
  isSidebarSticked: true,
  showHelpModal: false,
  view: initialView,
  dateRange: {
    startDate: subtractDaysAndFormat(30, "YYYY-MM-DD"),
    endDate: subtractDaysAndFormat(0, "YYYY-MM-DD"),
  },
  sideBarSections: SECTIONS,
  checkoutProfileId: undefined,
  breadcrumbValue: "",
  isUnsavedBarEnabled: false,
  isUnleashReady: false,
  isEligibleForReview: false,
  isBookDemoBannerVisible: true,
  isEnableAppBridgeModal: false,
};

export const loginAsync = createAsyncThunk("app/login", async (token: string, { getState }) => {
  const state = getState() as RootState;

  // If already logged in then don't need to login again. It happens when we removes the query params after login in useAppInit hook
  if (state.app.storeInfo) {
    return { response: { data: state.app.storeInfo } };
  }

  const response = await login(token);

  return response;
});

export const getCheckoutProfileIdAsync = createAsyncThunk("app/getCheckoutProfileIdAsync", async (_, { getState }) => {
  const state = getState() as RootState;

  const response = await getCheckoutProfileId(state.app.platformStoreId);

  return (response?.data?.id as string) || undefined;
});

export const fetchConditionsAndSlotsAsync = createAsyncThunk("app/fetchConditionsAndSlots", async (_, { getState }) => {
  const state = getState() as RootState;

  if (!state.app.activePage) {
    return [];
  }

  const [conditions, slots, wixSiteType] = await Promise.all([
    getAllCondition(state.app.activePage),
    getAllSlot(state.app.activePage),
    APP_PLATFORM === Platform.Wix && getWixSiteType(),
  ]);
  
  return { conditions, slots, activePage: state.app.activePage, platformStoreId: state.app.platformStoreId, wixSiteType };
});

export const fetchSlotsAsync = createAsyncThunk("app/fetchSlots", async (_, { getState }) => {
  const state = getState() as RootState;

  if (!state.app.activePage) {
    return [];
  }

  const slots = await getAllSlot(state.app.activePage);

  return { slots, activePage: state.app.activePage, platformStoreId: state.app.platformStoreId };
});

export const getSubscriptionDetailsAsync = createAsyncThunk("app/getSubscriptionDetails", async (_, { getState }) => {
  const state = getState() as RootState;

  if (!state.app.platformStoreId) {
    throw new Error("Platform store ID is required");
  }

  const response = await getSubscriptionDetails({});

  return {
    planId: response?.planId,
    isLatestPlan: response?.planId ? response.planId === LATEST_PLAN_ID : true,
    maxCap: Number(response?.maxCap || 0),
  };
});

export const updateSubscriptionAsync = createAsyncThunk("app/updateSubscription", async (data: any, { getState }) => {
  const state = getState() as RootState;

  if (!state.app.platformStoreId) {
    throw new Error("Platform store ID is required");
  }

  const response = await updateSubscription(data);

  return response;
});


const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setPlatformStoreId: (state, action: PayloadAction<string>) => {
      state.platformStoreId = action.payload;

      if (!state.platformStoreId) {
        return;
      }

      try {
        const localStorageItem = localStorage.getItem(String(state.platformStoreId)) || "{}";
        const localStorageValue = JSON.parse(localStorageItem);

        const isWidgetView = Object.hasOwn(localStorageValue, "isWidgetViewEnable")
          ? localStorageValue.isWidgetViewEnable
          : state.view.isWidgetView;
        const isAnalyticView = Object.hasOwn(localStorageValue, "isAnalyticViewEnable")
          ? localStorageValue?.isAnalyticViewEnable
          : state.view.isAnalyticView;

        state.view = {
          isWidgetView,
          isAnalyticView,
        };
      } catch (error) {
        console.log(error);
      }
    },
    setIsAppBridgePresent: (state, action: PayloadAction<boolean>) => {
      state.isAppBridgePresent = action.payload;
    },
    setStoreInfo: (state, action: PayloadAction<any>) => {
      state.storeInfo = action.payload;
      state.loading = false;
    },
    setActivePage: (state, action: PayloadAction<PageType | undefined>) => {
      state.activePage = action.payload;
    },
    toggleSideBar: state => {
      state.isSidebarCollapased = !state.isSidebarCollapased;
    },
    setIsSidebarSticked: (state, action: PayloadAction<boolean>) => {
      state.isSidebarSticked = action.payload;
    },
    setShowHelpModal: (state, action: PayloadAction<boolean>) => {
      state.showHelpModal = action.payload;
    },
    setHelpArticleLink: (state, action: PayloadAction<string | undefined>) => {
      state.helpArticleLink = action.payload;
    },
    toggleHelpModal: (state, action: PayloadAction<{ link: string; title: string } | undefined>) => {
      const iframeLink = action.payload;

      console.log('isEnableAppBridgeModal', state.isEnableAppBridgeModal);
      if (state.isEnableAppBridgeModal) {
        sendPostMessage(IFramePostMessageType.OPEN_HELP_MODAL, { link: iframeLink?.link, title: iframeLink?.title, reviewLocation: {
          widgetName: 'MODAL_WIDGET_V2',
          widgetLocation: 'FLOW_BUILDER_HELP_MODAL',
          locationForEvent: 'Flow builder help',
        } });
        return;
      } 

      if (iframeLink) {
        state.helpArticleLink = iframeLink.link;
        state.helpArticleTitle = iframeLink.title;
      }

      if (state.showHelpModal) {
        state.helpArticleLink = "";
        state.helpArticleTitle = "";
      }

      state.showHelpModal = !state.showHelpModal;
    },
    toggleWidgetView: state => {
      state.view = {
        ...state.view,
        isWidgetView: !state.view.isWidgetView,
      };

      const localStorageItem = localStorage.getItem(String(state.platformStoreId)) || "{}";
      const localStorageValue = JSON.parse(localStorageItem);
      const localStorageItems = {
        ...localStorageValue,
        isWidgetViewEnable: state.view.isWidgetView,
      };

      localStorage.setItem(`${state.platformStoreId}`, JSON.stringify(localStorageItems));
    },
    toggleAnalyticsView: state => {
      state.view = {
        ...state.view,
        isAnalyticView: !state.view.isAnalyticView,
      };

      const localStorageItem = localStorage.getItem(String(state.platformStoreId)) || "{}";
      const localStorageValue = JSON.parse(localStorageItem);
      const localStorageItems = {
        ...localStorageValue,
        isAnalyticViewEnable: state.view.isAnalyticView,
      };

      localStorage.setItem(`${state.platformStoreId}`, JSON.stringify(localStorageItems));
    },
    updateBreadcrumbValue: (state, action: PayloadAction<{ breadCrumb: string } | undefined>) => {
      state.breadcrumbValue = action.payload?.breadCrumb;
      console.log("@@@ FLOW-BUILDER > breadCrumb", action.payload?.breadCrumb);
      sendPostMessage(IFramePostMessageType.UPDATE_BREADCRUMB, { breadCrumb: action.payload?.breadCrumb });
    },
    setIFrameLink: (state, action: PayloadAction<string | undefined>) => {
      if (APP_PLATFORM === Platform.Wix) sessionStorage.setItem("flow_editor_url", action?.payload || "");

      state.iFrameLink = action.payload;
    },
    handleDateRangePicker: (
      state,
      action: PayloadAction<{
        startDate: Date | null;
        endDate: Date | null;
      }>,
    ) => {
      state.dateRange.startDate = dateFormatYYYYMMDD(action.payload.startDate!);
      state.dateRange.endDate = dateFormatYYYYMMDD(action.payload.endDate!);
    },
    updateSideBarSections: (state, action: PayloadAction<SideBarSection[]>) => {
      state.sideBarSections = action.payload;
    },
    toggleUnsavedBarValue: (state, action: PayloadAction<boolean>) => {
      state.isUnsavedBarEnabled = action.payload;
    },
    setUnleashReady: (state, action: PayloadAction<boolean>) => {
      state.isUnleashReady = action.payload;
    },
    setIsEligibleForReview: (state, action: PayloadAction<boolean>) => {
      state.isEligibleForReview = action.payload;
    },
    setBookDemoBannerVisible: (state, action: PayloadAction<boolean>) => {
      state.isBookDemoBannerVisible = action.payload;
    },
    setIsEnableAppBridgeModal: (state, action: PayloadAction<boolean>) => {
      state.isEnableAppBridgeModal = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginAsync.pending, state => {
        state.error = undefined;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        if (!action.payload.response.statusCode || action.payload.response.statusCode === 200) {
          state.storeInfo = {
            ...action.payload.response.data,
            isShopifyPlus: action.payload.response.data?.isShopifyPlus === 1 || action.payload.response?.data?.is_partner_development === 1,
          };
        } else {
          state.error = "Authentication failed";
          state.loading = false;
        }
      })
      .addCase(loginAsync.rejected, (state, error) => {
        console.log("app/login", { error });
        state.storeInfo = undefined;
        state.error = "Authentication failed";
        state.loading = false;
      })
      .addCase(getCheckoutProfileIdAsync.fulfilled, (state, action) => {
        state.checkoutProfileId = action.payload;
      })
      .addCase(getCheckoutProfileIdAsync.rejected, (state, error) => {
        console.log("app/getCheckoutProfileIdAsync", { error });
        state.checkoutProfileId = undefined;
      })
      .addCase(getSubscriptionDetailsAsync.fulfilled, (state, action) => {
        state.subscriptionDetails = action.payload;
      })
      .addCase(getSubscriptionDetailsAsync.rejected, (state, error) => {
        console.log("app/getSubscriptionDetailsAsync", { error });
        state.subscriptionDetails = undefined;
      })
      .addCase(updateSubscriptionAsync.fulfilled, (state, action) => {
        console.log("Subscription updated successfully", action.payload);
      })
      .addCase(updateSubscriptionAsync.rejected, (state, error) => {
        console.log("app/updateSubscriptionAsync", { error });
      });
  },
});

export const selectAppState = (state: RootState) => state.app;

export const {
  setLoading,
  setError,
  setPlatformStoreId,
  setIsAppBridgePresent,
  setStoreInfo,
  setActivePage,
  toggleSideBar,
  setIsSidebarSticked,
  setShowHelpModal,
  setHelpArticleLink,
  toggleHelpModal,
  toggleWidgetView,
  toggleAnalyticsView,
  setIFrameLink,
  handleDateRangePicker,
  updateSideBarSections,
  updateBreadcrumbValue,
  toggleUnsavedBarValue,
  setUnleashReady,
  setIsEligibleForReview,
  setBookDemoBannerVisible,
  setIsEnableAppBridgeModal,
} = appSlice.actions;

export default appSlice.reducer;
